/**
 * Where this visit came in through (GH-841), so the organization's Home can say where gifts come
 * from: a table's printed code (`?via=table_qr`, from the console's Donor QR), the big screen's code
 * (`?via=display_qr`, from the projector), or — anything else — a shared link or the web.
 *
 * The tag is read once from the address the donor landed on and kept for this tab only
 * (`sessionStorage`), because the gift is often made a few pages later: the wall's code opens
 * check-in, and the give button is on the room page after it. It is sent as `via` when the server
 * mints the checkout link, and nowhere else.
 *
 * **Attribution, not authority.** Nothing about the donor goes in it, the server accepts only its
 * four known tags, and the only consequence of a wrong one is which bar on Home grows.
 */
import { MEMBERSHIP_RULES, shareRefSchema } from "@givehere/contracts";

/**
 * The tags a printed or projected code — or the email-safe Give button (GH-847), which links to the
 * organization's page with `?via=email_button` — may carry into this bundle. `embed_widget` is not
 * one: the give box's tag is set by its own session route on the server, never by an address.
 *
 * The console's Buttons & links tool (GH-852) adds three: `payment_button` (the copy-paste button
 * on another website), `printed_qr` (its code for flyers) and `share_link` (its plain and Markdown
 * links — the default anyway, but named so it replaces an earlier tag in the same tab).
 */
const ARRIVAL_TAGS = [
  "table_qr",
  "display_qr",
  "email_button",
  "payment_button",
  "printed_qr",
  "share_link",
] as const;
export type ArrivalTag = (typeof ARRIVAL_TAGS)[number];
export type LinkTag = ArrivalTag;

const STORAGE_KEY = "givehere.arrivedVia";

const isArrivalTag = (value: string | null): value is ArrivalTag =>
  value !== null && (ARRIVAL_TAGS as readonly string[]).includes(value);

/** Keep the tag in the address this visit arrived with, if it carries a known one. */
export function rememberArrival(search: string = window.location.search): void {
  const via = new URLSearchParams(search).get("via");
  if (!isArrivalTag(via)) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, via);
  } catch {
    // Storage refused (private mode, quota): the gift reads as a shared link. Nothing breaks.
  }
}

/** The tag to mint a checkout link with: how this visit arrived, else a shared link or the web. */
export function linkTag(): LinkTag {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (isArrivalTag(stored)) return stored;
  } catch {
    // Unreadable storage: fall through to the honest default.
  }
  return "share_link";
}

/* ------------------------------------------------------------- member share code (GH-864e) */

/**
 * A member's share code, from `?ref=` on a link they shared (GH-864, DEC-051).
 *
 * Kept on this device for `MEMBERSHIP_RULES.shareRefRetentionDays` — the link-capture window — in
 * `localStorage` rather than the tab's `sessionStorage`, because a friend often opens a shared link,
 * closes it, and gives from the organization's page days later. It is sent as `ref` when the server
 * mints a checkout link and nowhere else; the server re-validates it and resolves it into the signed
 * context, so a forged or retired code degrades to no credit, never to an error.
 *
 * **Attribution, not authority**, exactly like `via`: the code names a member's link, never the
 * donor, and it can only decide whose points a gift adds to.
 *
 * Last touch wins: a later valid `ref` replaces an earlier one. A malformed one is dropped and does
 * not disturb a good one already kept.
 */
const REFERRAL_KEY = "givehere.ref";
const DAY_MS = 86_400_000;

type StoredReferral = { readonly code: string; readonly expiresAt: number };

function parseReferral(raw: string | null): StoredReferral | null {
  if (raw === null) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return null;
    const { code, expiresAt } = value as Record<string, unknown>;
    if (typeof code !== "string" || typeof expiresAt !== "number") return null;
    const valid = shareRefSchema.safeParse(code);
    return valid.success ? { code: valid.data, expiresAt } : null;
  } catch {
    return null;
  }
}

/** Keep the `ref` this visit arrived with, if it is a well-formed share code. */
export function rememberReferral(
  search: string = window.location.search,
  now: number = Date.now(),
): void {
  const raw = new URLSearchParams(search).get("ref");
  if (raw === null) return;
  const valid = shareRefSchema.safeParse(raw);
  if (!valid.success) return;
  const stored: StoredReferral = {
    code: valid.data,
    expiresAt: now + MEMBERSHIP_RULES.shareRefRetentionDays * DAY_MS,
  };
  try {
    window.localStorage.setItem(REFERRAL_KEY, JSON.stringify(stored));
  } catch {
    // Storage refused: the gift simply carries no member credit. Giving is unaffected.
  }
}

/** The kept share code, or null once it has lapsed — and then it is forgotten. */
export function referralCode(now: number = Date.now()): string | null {
  try {
    const stored = parseReferral(window.localStorage.getItem(REFERRAL_KEY));
    if (stored !== null && stored.expiresAt > now) return stored.code;
    window.localStorage.removeItem(REFERRAL_KEY);
  } catch {
    // Unreadable storage: no credit, and nothing else changes.
  }
  return null;
}
